import React, { useMemo } from 'react';
import { ArrowRight, Check } from 'lucide-react';
import { Button, SeverityPill } from '@/ui';
import { FIX_ACTIONS, FIX_ACTION_BY_KEY, type FixActionKey, type FixFinding } from './types';
import { formatCostRange } from './_format';

interface StepSelectActionsProps {
  step: 1 | 2;
  findings: FixFinding[];
  selected: Set<string>;
  actions: Map<string, FixActionKey>;
  credits: Map<string, number | undefined>;
  onToggle: (id: string) => void;
  onAction: (id: string, action: FixActionKey) => void;
  onCredit: (id: string, value: number | undefined) => void;
  onContinue: () => void;
}

export const StepSelectActions: React.FC<StepSelectActionsProps> = ({
  step, findings, selected, actions, credits, onToggle, onAction, onCredit, onContinue,
}) => {
  const grouped = useMemo(() => {
    const out = new Map<string, FixFinding[]>();
    for (const f of findings) {
      const list = out.get(f.sectionTitle) ?? [];
      list.push(f);
      out.set(f.sectionTitle, list);
    }
    return Array.from(out.entries());
  }, [findings]);

  const isPick = step === 1;

  return (
    <main className="max-w-[860px] mx-auto px-4 sm:px-8 pb-32">
      <header className="mt-6 sm:mt-8 mb-4">
        <div className="text-overline uppercase text-brand">Step {step} of 3</div>
        <h2 className="mt-1.5 font-display text-display-md text-ink leading-tight">
          {isPick ? 'Pick the items you want addressed' : 'Choose what should happen with each'}
        </h2>
        <p className="mt-2 text-body-md text-ink-subtle max-w-prose-narrow">
          {isPick
            ? 'Safety Concerns are pre-selected. Add or remove anything else you’d like to flag. You’ll choose what to do with each on the next step.'
            : 'Pick an action per item. Credit amounts are pre-filled from the AI estimate — adjust as you’d like.'}
        </p>
      </header>

      <div className="space-y-8 mt-6">
        {grouped.map(([section, items]) => {
          const visible = isPick ? items : items.filter(f => selected.has(f.id));
          if (visible.length === 0) return null;
          const selectedCount = visible.filter(f => selected.has(f.id)).length;
          return (
            <section key={section}>
              <div className="flex items-baseline justify-between gap-3 mb-3 pb-3 border-b border-border-subtle">
                <h3 className="font-sans text-heading-sm text-ink m-0">{section}</h3>
                <span className="font-mono tabular-nums text-caption text-ink-subtle">
                  {selectedCount} of {visible.length} selected
                </span>
              </div>
              <div className="space-y-3">
                {visible.map(f => (
                  <FindingRow
                    key={f.id}
                    f={f}
                    selected={selected.has(f.id)}
                    action={actions.get(f.id) ?? 'repair'}
                    creditValue={credits.get(f.id)}
                    onToggle={() => onToggle(f.id)}
                    onAction={(a) => onAction(f.id, a)}
                    onCredit={(v) => onCredit(f.id, v)}
                    showActions={step === 2}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {/* Mobile sticky footer — top bar's primary is hidden on small screens. */}
      <div
        className="fixed bottom-0 inset-x-0 z-30 sm:hidden border-t border-border-subtle bg-surface-0/95 backdrop-blur"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="max-w-[860px] mx-auto px-4 py-3 flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="font-display text-heading-md text-ink leading-none">
              {selected.size} {selected.size === 1 ? 'item' : 'items'}
            </div>
            <div className="text-caption text-ink-subtle mt-0.5">
              {isPick ? 'Selected' : 'Ready to review'}
            </div>
          </div>
          <Button
            variant="primary"
            onClick={onContinue}
            disabled={selected.size === 0}
            trailingIcon={<ArrowRight />}
          >
            {isPick ? 'Continue' : 'Review'}
          </Button>
        </div>
      </div>
    </main>
  );
};

interface FindingRowProps {
  f: FixFinding;
  selected: boolean;
  action: FixActionKey;
  creditValue: number | undefined;
  onToggle: () => void;
  onAction: (a: FixActionKey) => void;
  onCredit: (v: number | undefined) => void;
  showActions: boolean;
}

const FindingRow: React.FC<FindingRowProps> = ({
  f, selected, action, creditValue, onToggle, onAction, onCredit, showActions,
}) => {
  const open = showActions && selected;
  const actionDef = FIX_ACTION_BY_KEY[action];
  const sevTone = f.severity === 'safety_concern' ? 'safety_concern' : f.severity === 'recommendation' ? 'recommendation' : null;

  return (
    <article
      className={
        'overflow-hidden rounded-lg border bg-surface-1 shadow-card transition-[border-color,box-shadow,opacity] duration-180 ' +
        (selected ? 'border-brand/60 ring-1 ring-brand/40 ' : 'border-border-subtle ') +
        (!showActions && !selected ? 'opacity-60' : '')
      }
    >
      <div className="grid grid-cols-[28px_1fr] sm:grid-cols-[32px_1fr_auto] gap-3 sm:gap-4 p-4 sm:p-5 items-start">
        <button
          type="button"
          role="checkbox"
          aria-checked={selected}
          aria-label={selected ? 'Remove from fix list' : 'Add to fix list'}
          onClick={onToggle}
          className={
            'mt-0.5 h-5 w-5 shrink-0 rounded-[5px] border inline-flex items-center justify-center transition-colors duration-180 ' +
            (selected
              ? 'bg-brand border-brand text-brand-ink hover:bg-brand/90'
              : 'bg-surface-1 border-ink-faint text-transparent hover:border-ink-subtle')
          }
        >
          {selected && <Check className="h-3 w-3" strokeWidth={3} />}
        </button>

        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {sevTone && <SeverityPill severity={f.severity} compact />}
            <span className="font-mono tabular-nums text-[11px] font-semibold tracking-wider uppercase text-ink-faint">
              {f.sectionTitle} · {f.indexLabel}
            </span>
          </div>
          <h3 className="mt-1 font-sans text-body-md font-semibold text-ink leading-snug">{f.title}</h3>
          {f.description && (
            <p className="mt-1 text-body-sm text-ink-subtle leading-relaxed max-w-prose-narrow">{f.description}</p>
          )}
        </div>

        <aside className="col-span-2 sm:col-span-1 sm:text-right shrink-0 sm:pt-0.5">
          <div className="font-sans text-[10.5px] uppercase tracking-wider font-semibold text-ink-faint">AI estimate</div>
          <div className="mt-0.5 font-mono tabular-nums text-caption text-ink-muted">
            {formatCostRange(f.estimatedCostLow, f.estimatedCostHigh, f.estimatedCost)}
          </div>
        </aside>
      </div>

      {open && (
        <div className="border-t border-dashed border-border-subtle px-4 sm:px-5 sm:ml-[46px] py-3.5 sm:py-4 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-overline uppercase text-ink-subtle shrink-0">Action</span>
            <div className="flex flex-wrap gap-1.5 min-w-0">
              {FIX_ACTIONS.map(a => {
                const on = a.key === action;
                return (
                  <button
                    key={a.key}
                    type="button"
                    onClick={() => onAction(a.key)}
                    className={
                      'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-caption font-medium border transition-colors duration-180 ' +
                      (on
                        ? 'bg-brand text-brand-ink border-brand hover:brightness-95'
                        : 'bg-surface-1 text-ink-muted border-border hover:text-ink hover:border-border-strong')
                    }
                  >
                    {/* The colored dot reads the action; on the brand-fill
                        selected state it'd compete with the bg, so drop it. */}
                    {!on && <span className={`h-1.5 w-1.5 rounded-full ${a.dotClass}`} />}
                    {a.label}
                  </button>
                );
              })}
            </div>
          </div>
          {actionDef.showCredit && (
            <label className="flex items-center justify-end gap-2 max-w-full">
              <span className="text-body-sm text-ink-subtle shrink-0">Credit</span>
              <span className="flex items-center gap-0 px-2.5 h-9 rounded-md border border-border bg-surface-1 focus-within:border-brand focus-within:shadow-ring-brand w-full sm:w-[180px]">
                <span className="font-mono tabular-nums text-body-sm text-ink-subtle pr-1">$</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={creditValue == null ? '' : String(creditValue)}
                  placeholder={f.creditMid != null ? f.creditMid.toLocaleString('en-US') : ''}
                  onChange={(e) => {
                    const cleaned = e.target.value.replace(/[^0-9]/g, '');
                    onCredit(cleaned === '' ? undefined : Number(cleaned));
                  }}
                  className="flex-1 min-w-0 bg-transparent border-0 outline-none font-mono tabular-nums text-body-sm text-ink placeholder:text-ink-faint p-0"
                />
              </span>
            </label>
          )}
        </div>
      )}
    </article>
  );
};
